import React from 'react';

import Card from '@app/components/UI/Card/Card';
import Footprint from '@app/components/UI/Footprint/Footprint';
import Star from '@app/imgs/Star';

const tabs: Array<{
	img: string;
	title: string;
	amazon: string;
	desc: React.ReactNode;
}> = [
	{
		img: '/images/image_357.png',
		title: 'It’s Never Long Enough: A practical guide to caring for your geriatric dog',
		amazon: 'https://amzn.to/3JvpQqA',
		desc: (
			<>
				<p>
					It’s Never Long Enough draws from thousands of Dr. Mary’s visits with her grey muzzle dog patients, a lifetime of heartfelt tales
					about her own elderly dogs, and recent studies in animal and human health related to aging and the ailments that may come with it.
					Dr. Mary Gardner’s compassionate guide explains the aging process in dogs, prepares pet parents for what comes next, and coaches
					them on caring for their geriatric dog. You’ll learn:
				</p>
				<ul>
					<li>ways to extend and improve your dog’s later years,</li>
					<li>how to identify common ailments in elderly dogs,</li>
					<li>home hacks to manage daily living and care of your geriatric dog,</li>
					<li>warning signs that may need veterinary assistance,</li>
					<li>how to choose the best medical care for your situation,</li>
					<li>key questions to ask your veterinarian,</li>
					<li>helpful resources and product suggestions from affordable to high-tech,</li>
					<li>options for aftercare,</li>
					<li>resources for managing caregiver burnout and grief,</li>
					<li>ways to honor your dog and their impact on your life.</li>
				</ul>
				<p>One truth remains: regardless of the time we have with our dogs, it’s never long enough.</p>
			</>
		),
	},
	{
		img: '/images/nine-lives-are-not-enough-practical-guide-to-caring-for-your-geriatric-cat.jpeg',
		title: 'Nine Lives Are Not Enough: practical guide to caring for your geriatric cat',
		amazon: 'https://www.amazon.com/Nine-Lives-Are-Not-Enough/dp/1956343083/',
		desc: (
			<>
				<p>
					Nine Lives Are Not Enough draws from thousands of Dr. Mary’s visits with her senior and terminally ill cat patients, a lifetime of
					heartfelt tales about her own elderly cats, and recent studies in animal and human health related to aging and the ailments that
					may come with it. Dr. Mary Gardner’s compassionate guide explains the aging process in cats, prepares pet parents for what comes
					next, and coaches them on caring for their aging cat. You’ll learn:
				</p>
				<ul>
					<li>ways to extend and improve your cat’s later years,</li>
					<li>how to identify common ailments in elderly cats,</li>
					<li>home hacks to manage daily living and care of your geriatric cat,</li>
					<li>warning signs that may need veterinary assistance,</li>
					<li>how to choose the best medical care for your situation,</li>
					<li>key questions to ask your veterinarian,</li>
					<li>helpful resources and product suggestions from affordable to high-tech,</li>
					<li>options for aftercare,</li>
					<li>resources for managing caregiver burnout and grief,</li>
					<li>ways to honor your cat and their impact on your life.</li>
				</ul>
				<p>One truth remains: regardless of the time we have with our cats, certainly nine lives are not enough!</p>
			</>
		),
	},
	{
		img: '/images/geriatric-cat-health-care-journal-a-complete-toolkit-for-the-geriatric-cat-caregiver.jpg',
		title: 'Geriatric Cat Health & Care Journal: A complete toolkit for the geriatric cat caregiver',
		amazon: 'https://amzn.to/3xo0as3',
		desc: (
			<>
				<p>
					The Geriatric Cat Health & Care Journal helps caregivers identify factors that may have contributed to the undesirable or
					desirable changes or symptoms they note in their aging cat.
				</p>
				<p>
					The journal pages, questionnaires, logs, lists, assessments, and other templates provided will help pet parents document and keep
					important information handy and organized. This journal can also make partnering with a primary care veterinarian easier as
					continued care to the cat is provided.
				</p>
				<p className="h3">Provided in the journal:</p>
				<ul>
					<li>My cat’s care team;</li>
					<li>Health journal;</li>
					<li>Meals and treats;</li>
					<li>Cognitive health assessment;</li>
					<li>Symptom record;</li>
					<li>Veterinary visits and procedures;</li>
					<li>Diagnostic tests;</li>
					<li>Tracking treatments;</li>
					<li>Counting breaths;</li>
					<li>Goals of care;</li>
					<li>The adventures of a lifetime;</li>
					<li>Joyful living;</li>
					<li>Assessing your cat’s life quality;</li>
					<li>Caring for caregivers – their QOL;</li>
					<li>Cat days calendar.</li>
				</ul>
			</>
		),
	},
	{
		img: '/images/recommendation2.png',
		title: 'Geriatric Dog Health & Care Journal: A complete toolkit for the geriatric dog caregiver',
		amazon: 'https://amzn.to/32SKUq0',
		desc: (
			<>
				<p>
					The Geriatric Dog Health & Care Journal helps caregivers identify factors that may have contributed to the undesirable or
					desirable changes or symptoms they note in their aging dog.
				</p>
				<p>
					The journal pages, questionnaires, logs, lists, assessments, and other templates provided will help pet parents document and keep
					important information handy and organized. This journal can also make partnering with a primary care veterinarian easier as
					continued care to the dog is provided.
				</p>
				<p>Provided in the journal:</p>
				<ul>
					<li>My dog’s care team;</li>
					<li>Health journal;</li>
					<li>Meals and treats;</li>
					<li>Cognitive health assessment;</li>
					<li>Symptom record;</li>
					<li>Veterinary visits and procedures;</li>
					<li>Diagnostic tests;</li>
					<li>Tracking treatments;</li>
					<li>Counting breaths;</li>
					<li>Goals of care;</li>
					<li>The adventures of a lifetime;</li>
					<li>Joyful living;</li>
					<li>Assessing your dog’s life quality;</li>
					<li>Caring for caregivers – their QOL;</li>
					<li>Dog days calendar.</li>
				</ul>
			</>
		),
	},
];

const EducationalBooks: React.FC = () => {
	const [tabActive, setTabActive] = React.useState(0);

	return (
		<Card
			image={
				<div className="tabs">
					{tabs.map((tab, i) => (
						<div key={i} className={`tab text-primary ${tabActive === i && 'active'}`} onClick={() => setTabActive(i)}>
							<div className="shadow-pictures">
								<img src={tab.img} alt="book" />
							</div>
							<p>
								{tab.title}
								<span>
									<Footprint />
								</span>
							</p>
						</div>
					))}
				</div>
			}
			text={
				<div className="text-secondary">
					<div className="text">
						<div className="shadow-pictures">
							<img src={tabs[tabActive].img} alt={`Book - ${tabs[tabActive].title}`} />
						</div>
						<div className="info-book">
							<div className="h2">{tabs[tabActive].title}</div>
							<div className="rating-block">
								<div>
									<div>Hardcover – January 17, 2022</div>
									<div className="rating">
										<div>
											<strong>4.8</strong>
										</div>
										<div className="block-stars">
											<Star color="#ffdd60" />
											<Star color="#ffdd60" />
											<Star color="#ffdd60" />
											<Star color="#ffdd60" />
											<Star color="#ffdd60" />
										</div>
										<div>
											<a href="#">67 ratings</a>
										</div>
									</div>
								</div>
								<div>
									<a href={tabs[tabActive].amazon} target="_blank" rel="noreferrer nofollow">
										<img src="/images/amazon.png" alt="amazon" />
									</a>
								</div>
							</div>
						</div>
					</div>
					<div className="description">
						<p className="title text-primary">Description</p>
						{tabs[tabActive].desc}
					</div>
				</div>
			}
		/>
	);
};

export default EducationalBooks;
